/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import classNames from 'classnames'
import { useSearchParams } from 'next/navigation'
import { usePathname, useRouter } from 'lib/i18n/routing'
import { useState, useEffect, useCallback } from 'react'
import { m } from 'motion/react'
import { MDSButton } from '@hashicorp/mds-react/components'

import InlineLookups from './InlineLookups'
import { Products } from '@web/product-meta'
import s from './style.module.css'

export interface IntegrationDetailsProps {
	companyName: string
	integrations: Array<{
		description: string
		getStartedLink?: string | null
		documentationLink?: string | null
		product: { name: string }
	}>
	activeProducts: string[]
	inactiveProducts: string[]
}

function CloudIntegrationDetails({
	companyName,
	integrations,
	activeProducts,
	inactiveProducts,
}: IntegrationDetailsProps): React.ReactElement {
	const router = useRouter()
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const [activeIntegration, setActiveIntegration] = useState(null)

	useEffect(() => {
		if (searchParams.get('product')) {
			setActiveIntegration(searchParams.get('product'))
			const integrationSection = document.querySelector('#integration-details')

			window.scrollTo(
				0,
				integrationSection.getBoundingClientRect().top + window.pageYOffset,
			)
		} else {
			setActiveIntegration(integrations[0].product.name.toLowerCase())
		}
	}, [searchParams.get('product')])

	/**
	 * Updates the page's search params to include
	 * a product param.
	 */
	const handleUpdateIntegration = useCallback(
		(product: string) => {
			// Convert search params to array of URL-ready strings, with product added
			const searchParamsWithProduct = { ...searchParams, product }

			const searchParamsAsStrings = []

			for (const [key, value] of Object.entries(searchParamsWithProduct)) {
				searchParamsAsStrings.push(`${key}=${value}`)
			}

			// Adjust the URL to include the updated search params
			let adjustedUrl = pathname

			if (searchParamsAsStrings?.length >= 1) {
				adjustedUrl = pathname + `?` + searchParamsAsStrings.join('&')
			}

			router.replace(adjustedUrl, { scroll: false })
			setActiveIntegration(product)
		},
		[setActiveIntegration],
	)

	return (
		<section id="integration-details" className={s.integrationDetails}>
			<h5 className={s.label}>Integration</h5>
			<h2 className={s.title}>
				How {companyName} works with HashiCorp Product Suite
			</h2>
			<div className={s.integrationInformation}>
				<nav className={s.productsList}>
					<ul>
						{activeProducts.map((product) => (
							<li key={product}>
								<button
									onClick={() => handleUpdateIntegration(product)}
									className={`${s[product]} ${s.enabled} ${
										activeIntegration === product ? s.active : ''
									}`}
								>
									<InlineLookups product={product} />
								</button>
							</li>
						))}
						{inactiveProducts.map((product) => (
							<li key={product}>
								<a className={s.disabled}>
									<InlineLookups product={product} />
								</a>
							</li>
						))}
					</ul>
				</nav>
				<div className={s.integrationDescriptionContainer}>
					{integrations.map((integration) => {
						const productSlug = integration.product.name.toLowerCase()
						const isActive = activeIntegration === productSlug

						return (
							<m.div
								className={classNames(s.integrationDescription, s[productSlug])}
								variants={variants}
								style={{ opacity: 0, zIndex: -1 }}
								animate={isActive ? 'active' : 'inactive'}
								inert={!isActive ? true : undefined}
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								forceMount
								key={integration.product.name}
								id={`${productSlug}-description`}
							>
								<div
									dangerouslySetInnerHTML={{
										__html: integration.description,
									}}
								/>
								{integration.getStartedLink && (
									<MDSButton
										className={s.button}
										title={`Get Started with ${integration.product.name}`}
										url={integration.getStartedLink}
										theme={{
											brand: productSlug as Products,
										}}
									/>
								)}
								{integration.documentationLink && (
									<MDSButton
										title="Read Documentation"
										url={integration.documentationLink}
										theme={{
											variant: 'secondary',
										}}
									/>
								)}
							</m.div>
						)
					})}
				</div>
			</div>
		</section>
	)
}

const variants = {
	active: {
		opacity: 1,
		zIndex: 1,
		transition: {
			duration: 0.3,
			delay: 0.3,
		},
	},
	inactive: {
		opacity: 0,
		zIndex: -1,
		transition: {
			duration: 0.3,
		},
	},
}

export default CloudIntegrationDetails
